/**
 * ImageEditPanel Component
 *
 * Settings Panel (Rechts) - Task Selector, Prompt, Controls
 * @accessibility WCAG 2.1 AA konform
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button, Textarea } from '@/components/v2/ui';
import { TaskSelector } from './TaskSelector';
import { Loader2 } from 'lucide-react';
import { getCreditPricing } from '@/lib/api/credits';
import type { EditTask } from '@/hooks/useImageEditor';

interface ImageEditPanelProps {
  selectedTask: EditTask;
  onTaskChange: (task: EditTask) => void;
  onGenerate: (params: GenerateParams) => void;
  isGenerating: boolean;
  disabled?: boolean;
}

interface GenerateParams {
  prompt: string;
  numberOfImages: number;
  negativePrompt?: string;
}

export function ImageEditPanel({
  selectedTask,
  onTaskChange,
  onGenerate,
  isGenerating,
  disabled = false,
}: ImageEditPanelProps) {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [numberOfImages, setNumberOfImages] = useState(1);
  const [negativePrompt, setNegativePrompt] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleGenerate = () => {
    if (!prompt.trim()) return;
    onGenerate({
      prompt: prompt.trim(),
      numberOfImages,
      negativePrompt: negativePrompt.trim() || undefined,
    });
  };

  const { data: pricingData } = useQuery({
    queryKey: ['credit-pricing'],
    queryFn: getCreditPricing,
    staleTime: 60 * 60 * 1000,
  });
  const creditsPerImage = pricingData?.pricing?.find(
    p => p.feature_code === 'image_generation'
  )?.effective_credits ?? 0;
  const totalCost = numberOfImages * creditsPerImage;

  return (
    <div className="image-edit-panel flex flex-col h-full p-6 bg-white dark:bg-neutral-900 overflow-y-auto">
      <h2 className="text-lg font-semibold mb-4 text-neutral-900 dark:text-neutral-100">
        Bearbeitung
      </h2>

      {/* Task Selector */}
      <div className="mb-6">
        <TaskSelector value={selectedTask} onChange={onTaskChange} />
      </div>

      {/* Prompt Input - IMMER aktiviert! */}
      <div className="mb-6">
        <label htmlFor="prompt" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
          {getPromptLabel(selectedTask, t)}
        </label>
        <Textarea
          id="prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={getPromptPlaceholder(selectedTask)}
          rows={4}
          disabled={isGenerating}
          className="w-full"
        />
      </div>

      {/* Number of Images - IMMER wählbar! */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
          Anzahl Varianten
        </label>
        <div className="flex gap-2">
          {[1, 2, 3, 4].map((num) => (
            <button
              key={num}
              onClick={() => setNumberOfImages(num)}
              disabled={isGenerating}
              className={`px-4 py-2 border rounded transition-colors ${
                numberOfImages === num
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                  : 'border-neutral-300 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800'
              } ${isGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
              aria-label={`${num} Variante${num > 1 ? 'n' : ''}`}
            >
              {num}
            </button>
          ))}
        </div>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
          Kosten: {totalCost} Credits
        </p>
      </div>

      {/* Advanced Settings (Collapsible) */}
      <details className="mb-6" open={showAdvanced} onToggle={(e) => setShowAdvanced((e.target as HTMLDetailsElement).open)}>
        <summary className="text-sm font-medium text-neutral-700 dark:text-neutral-300 cursor-pointer mb-2">
          Erweitert
        </summary>
        <div className="space-y-4 mt-2">
          <div>
            <label htmlFor="negative-prompt" className="block text-sm text-neutral-600 dark:text-neutral-400 mb-2">
              Negative Prompt (Optional)
            </label>
            <Textarea
              id="negative-prompt"
              value={negativePrompt}
              onChange={(e) => setNegativePrompt(e.target.value)}
              placeholder="Was NICHT im Bild sein soll..."
              rows={3}
              disabled={isGenerating}
              className="w-full"
            />
          </div>
        </div>
      </details>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Generate Button */}
      <Button
        variant="primary"
        onClick={handleGenerate}
        disabled={!prompt.trim() || disabled || isGenerating}
        className="w-full"
        aria-label="Bild generieren"
      >
        {isGenerating ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {t('common.generating')}
          </>
        ) : (
          t('common.generate')
        )}
      </Button>
    </div>
  );
}

function getPromptLabel(task: EditTask, t: (key: string) => string): string {
  switch (task) {
    case 'inpaint_add':
      return t('editor.whatToAdd');
    case 'inpaint_remove':
      return t('editor.whatToRemove');
    case 'outpaint':
      return 'Beschreibe die Erweiterung';
    case 'background_edit':
      return 'Neuer Hintergrund';
  }
}

function getPromptPlaceholder(task: EditTask): string {
  switch (task) {
    case 'inpaint_add':
      return 'z.B. ein rotes Sportauto';
    case 'inpaint_remove':
      return 'z.B. Person im Hintergrund';
    case 'outpaint':
      return 'z.B. Erweitere natürlich mit Meer';
    case 'background_edit':
      return 'z.B. Strand bei Sonnenuntergang';
  }
}
